import React, { useState } from "react";
import { C, ROLES, ROLE_NAMES, ROLE_PERMISSIONS } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, useToast
} from "./erp-shared";
import {
  UserCog, Users, UserPlus, Plus, Search, CheckCircle, Clock,
  AlertCircle, Shield, Lock, Eye, Edit, Trash2, Calendar,
  Mail, Phone, Key, Activity, LogIn, LogOut, FileText,
  ShieldCheck, ShieldOff, RefreshCw, Hash, Settings, XCircle
} from "lucide-react";

export default function UsersPage({ user }) {
  const [tab, setTab] = useState("users");
  const perms = ROLE_PERMISSIONS[user.role] || {};

  const tabs = [
    { key: "users", label: "المستخدمون", icon: Users },
    { key: "roles", label: "الأدوار والصلاحيات", icon: Shield },
    { key: "audit", label: "سجل النظام", icon: Activity },
    { key: "sessions", label: "الجلسات النشطة", icon: LogIn },
  ];

  return (
    <div>
      <ModuleHeader icon={UserCog} title="المستخدمون" subtitle="إدارة حسابات المستخدمين والأدوار والصلاحيات وسجل النظام" />
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "users" && <UsersTab canEdit={perms.canEdit || perms.adminOnly} currentUser={user} />}
      {tab === "roles" && <RolesTab />}
      {tab === "audit" && <AuditTab />}
      {tab === "sessions" && <SessionsTab />}
    </div>
  );
}

/* ─── Users Tab ─── */
const INITIAL_USER = { name: "", email: "", phone: "", roleLabel: "", password: "" };
const INITIAL_RESET = { password: "" };

function UsersTab({ canEdit, currentUser }) {
  const [users, setUsers] = useState(DEMO_USERS);
  const [search, setSearch] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [form, setForm] = useState(INITIAL_USER);
  const [resetForm, setResetForm] = useState(INITIAL_RESET);
  const [showToast, ToastView] = useToast();

  const roles = Object.keys(ROLE_NAMES);
  const labelToRoleKey = Object.fromEntries(roles.map(r => [ROLE_NAMES[r], r]));

  function handleSave() {
    if (!form.name || !form.email || !form.roleLabel || !form.password) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const newU = {
      id: `u${Date.now()}`,
      name: form.name,
      email: form.email,
      phone: form.phone || "",
      role: labelToRoleKey[form.roleLabel] || "employee",
      is_active: true,
      must_change_password: true,
      last_login: null,
      created_at: new Date().toISOString().slice(0, 10),
    };
    setUsers(prev => [newU, ...prev]);
    setForm(INITIAL_USER);
    setShowAdd(false);
    showToast("تم إنشاء الحساب");
  }

  function handleReset() {
    if (!resetForm.password) {
      showToast("كلمة المرور مطلوبة", "error");
      return;
    }
    setUsers(prev => prev.map(u => u.id === showResetPw.id ? { ...u, must_change_password: true } : u));
    setResetForm(INITIAL_RESET);
    setShowResetPw(null);
    showToast("تم إعادة تعيين كلمة المرور");
  }
  const filtered = users.filter(u => {
    const ms = u.name.includes(search) || u.email.includes(search);
    const mr = filterRole === "all" || u.role === filterRole;
    return ms && mr;
  });

  const USER_STATUSES = [
    { key: "active", label: "فعّال", color: C.success },
    { key: "inactive", label: "معطّل", color: C.danger },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {ToastView}
      <div style={S.kpiRow}>
        <KPICard title="إجمالي المستخدمين" value={users.length} icon={Users} color="#475569" />
        <KPICard title="فعّالين" value={users.filter(u => u.is_active).length} icon={CheckCircle} color={C.success} />
        <KPICard title="معطّلين" value={users.filter(u => !u.is_active).length} icon={XCircle} color={C.danger} />
        <KPICard title="الأدوار" value={[...new Set(users.map(u => u.role))].length} icon={Shield} color="#7c3aed" />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={S.toolbar}>
          <div style={S.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو البريد..." value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={S.filterSelect}>
              <option value="all">جميع الأدوار</option>
              {roles.map(r => <option key={r} value={r}>{ROLE_NAMES[r]}</option>)}
            </select>
            {canEdit && <Btn icon={Plus} onClick={() => setShowAdd(true)} size="sm">إضافة مستخدم</Btn>}
          </div>
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "name", label: "المستخدم", render: (v, row) => (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{
                  ...S.tableAvatar,
                  background: row.is_active ? `linear-gradient(135deg, ${C.primary}, ${C.teal})` : C.textLight,
                }}>{v.charAt(0)}</div>
                <div>
                  <span style={{ fontWeight: 600, display: "block" }}>{v}</span>
                  <span style={{ fontSize: 11, color: C.textLight }} dir="ltr">{row.email}</span>
                </div>
              </div>
            )},
            { key: "role", label: "الدور", width: "140px", render: v => (
              <span style={{ padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: `#7c3aed12`, color: "#7c3aed" }}>{ROLE_NAMES[v]}</span>
            )},
            { key: "is_active", label: "الحالة", width: "90px", render: v => (
              <StatusBadge status={v ? "active" : "inactive"} statuses={USER_STATUSES} />
            )},
            { key: "last_login", label: "آخر دخول", width: "120px", render: v => v || "لم يسجّل دخول" },
            { key: "created_at", label: "تاريخ الإنشاء", width: "110px" },
            { key: "actions", label: "إجراءات", width: "100px", render: (v, row) => canEdit ? (
              <div style={{ display: "flex", gap: 4 }}>
                <button onClick={e => { e.stopPropagation(); setShowResetPw(row); }} style={S.actionBtn} title="إعادة تعيين كلمة المرور">
                  <Key size={14} color={C.warning} />
                </button>
                <button onClick={e => { e.stopPropagation(); }} style={S.actionBtn} title={row.is_active ? "تعطيل" : "تفعيل"}>
                  {row.is_active ? <ShieldOff size={14} color={C.danger} /> : <ShieldCheck size={14} color={C.success} />}
                </button>
              </div>
            ) : null },
          ]}
          data={filtered}
          onRowClick={row => setSelected(row)}
        />
      </Card>

      {/* Detail */}
      {selected && (
        <Modal title={`مستخدم — ${selected.name}`} onClose={() => setSelected(null)} width={550}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={S.userHeader}>
              <div style={S.userAvatarLg}>{selected.name.charAt(0)}</div>
              <div>
                <h3 style={{ margin: 0, color: C.primary }}>{selected.name}</h3>
                <p style={{ margin: "2px 0", color: "#7c3aed", fontWeight: 600, fontSize: 14 }}>{ROLE_NAMES[selected.role]}</p>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={S.detailRow}><strong>البريد:</strong> <span dir="ltr">{selected.email}</span></div>
              <div style={S.detailRow}><strong>الهاتف:</strong> <span dir="ltr">{selected.phone || "—"}</span></div>
              <div style={S.detailRow}><strong>الحالة:</strong> {selected.is_active ? "فعّال" : "معطّل"}</div>
              <div style={S.detailRow}><strong>آخر دخول:</strong> {selected.last_login || "لم يسجّل"}</div>
              <div style={S.detailRow}><strong>تاريخ الإنشاء:</strong> {selected.created_at}</div>
              <div style={S.detailRow}><strong>تغيير كلمة المرور:</strong> {selected.must_change_password ? "مطلوب" : "لا"}</div>
            </div>
            <div style={S.detailRow}>
              <strong>الصلاحيات:</strong>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                {(ROLE_PERMISSIONS[selected.role]?.modules || []).map((m, i) => (
                  <span key={i} style={S.permTag}>{m}</span>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
              {ROLE_PERMISSIONS[selected.role]?.canEdit && <span style={{ ...S.permBadge, background: `${C.teal}15`, color: C.teal }}>تعديل</span>}
              {ROLE_PERMISSIONS[selected.role]?.canSign && <span style={{ ...S.permBadge, background: `${C.accent}15`, color: "#8b6914" }}>توقيع</span>}
              {ROLE_PERMISSIONS[selected.role]?.adminOnly && <span style={{ ...S.permBadge, background: `${C.danger}15`, color: C.danger }}>مشرف</span>}
            </div>
          </div>
        </Modal>
      )}

      {/* Add User */}
      {showAdd && (
        <Modal title="إضافة مستخدم جديد" onClose={() => { setShowAdd(false); setForm(INITIAL_USER); }} width={600}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="الاسم الكامل" required placeholder="اسم المستخدم" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="البريد الإلكتروني" required placeholder="user@bnr.org.sa" value={form.email} onChange={v => setForm({ ...form, email: v })} />
              <Field label="الهاتف" placeholder="05XXXXXXXX" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="الدور" type="select" options={roles.map(r => ROLE_NAMES[r])} required value={form.roleLabel} onChange={v => setForm({ ...form, roleLabel: v })} />
              <Field label="كلمة المرور المؤقتة" required placeholder="كلمة مرور أولية" value={form.password} onChange={v => setForm({ ...form, password: v })} />
            </div>
            <div style={{ padding: "10px 14px", background: `${C.warning}10`, borderRadius: 8, fontSize: 13, color: C.warning, display: "flex", alignItems: "center", gap: 8 }}>
              <AlertCircle size={16} />
              سيُطلب من المستخدم تغيير كلمة المرور عند أول دخول
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={UserPlus} onClick={handleSave}>إنشاء الحساب</Btn>
              <Btn variant="ghost" onClick={() => { setShowAdd(false); setForm(INITIAL_USER); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset Password */}
      {showResetPw && (
        <Modal title={`إعادة تعيين كلمة المرور — ${showResetPw.name}`} onClose={() => { setShowResetPw(null); setResetForm(INITIAL_RESET); }} width={450}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <p style={{ fontSize: 14, color: C.primary, lineHeight: 1.6 }}>
              سيتم إعادة تعيين كلمة المرور للمستخدم <strong>{showResetPw.name}</strong> ({showResetPw.email})
              وسيُطلب منه تغييرها عند الدخول القادم.
            </p>
            <Field label="كلمة المرور الجديدة المؤقتة" required placeholder="كلمة مرور مؤقتة" value={resetForm.password} onChange={v => setResetForm({ ...resetForm, password: v })} />
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <Btn icon={Key} variant="danger" onClick={handleReset}>إعادة التعيين</Btn>
              <Btn variant="ghost" onClick={() => { setShowResetPw(null); setResetForm(INITIAL_RESET); }}>إلغاء</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Roles Tab ─── */
function RolesTab() {
  const allRoles = Object.keys(ROLE_NAMES);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={S.kpiRow}>
        <KPICard title="عدد الأدوار" value={allRoles.length} icon={Shield} color="#7c3aed" />
        <KPICard title="أدوار إدارية" value={allRoles.filter(r => ROLE_PERMISSIONS[r]?.adminOnly).length} icon={Lock} color={C.danger} />
        <KPICard title="أدوار تحرير" value={allRoles.filter(r => ROLE_PERMISSIONS[r]?.canEdit).length} icon={Edit} color={C.teal} />
      </div>

      <div style={S.rolesGrid}>
        {allRoles.map(roleKey => {
          const perm = ROLE_PERMISSIONS[roleKey] || {};
          return (
            <Card key={roleKey} style={S.roleCard}>
              <div style={S.roleHeader}>
                <div style={{ ...S.roleIcon, background: perm.adminOnly ? `${C.danger}12` : `#7c3aed12` }}>
                  {perm.adminOnly ? <Lock size={20} color={C.danger} /> : <Shield size={20} color="#7c3aed" />}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.primary }}>{ROLE_NAMES[roleKey]}</h4>
                  <p style={{ margin: "2px 0 0", fontSize: 12, color: C.textLight, fontFamily: "monospace" }}>{roleKey}</p>
                </div>
              </div>

              {/* Permission badges */}
              <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                {perm.adminOnly && <span style={{ ...S.permBadge, background: `${C.danger}15`, color: C.danger }}>مشرف كامل</span>}
                {perm.canEdit && <span style={{ ...S.permBadge, background: `${C.teal}15`, color: C.teal }}>تعديل</span>}
                {perm.canSign && <span style={{ ...S.permBadge, background: `${C.accent}15`, color: "#8b6914" }}>توقيع واعتماد</span>}
                {!perm.canEdit && !perm.adminOnly && <span style={{ ...S.permBadge, background: `${C.textLight}15`, color: C.textLight }}>قراءة فقط</span>}
              </div>

              {/* Modules access */}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.textLight }}>الوصول للموديولات ({perm.modules?.length || 0}):</span>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                  {(perm.modules || []).map((m, i) => (
                    <span key={i} style={S.moduleTag}>{m}</span>
                  ))}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Audit Tab ─── */
function AuditTab() {
  const [search, setSearch] = useState("");
  const filtered = DEMO_AUDIT_LOG.filter(l => l.username.includes(search) || l.action.includes(search) || l.details.includes(search));

  return (
    <div style={{ marginTop: 16 }}>
      <div style={S.kpiRow}>
        <KPICard title="إجمالي السجلات" value={DEMO_AUDIT_LOG.length} icon={Activity} color="#475569" />
        <KPICard title="تسجيلات دخول" value={DEMO_AUDIT_LOG.filter(l => l.action === "login").length} icon={LogIn} color={C.teal} />
        <KPICard title="تعديلات" value={DEMO_AUDIT_LOG.filter(l => l.action.includes("update") || l.action.includes("edit")).length} icon={Edit} color={C.warning} />
      </div>

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={S.searchBox}>
          <Search size={16} color={C.textLight} />
          <input type="text" placeholder="بحث في السجل..." value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
        </div>
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "timestamp", label: "التاريخ والوقت", width: "150px", render: v => <span style={{ fontSize: 12, fontFamily: "monospace" }}>{v}</span> },
            { key: "username", label: "المستخدم", width: "140px", render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
            { key: "action", label: "الإجراء", width: "130px", render: v => (
              <span style={{
                padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                background: v === "login" ? `${C.teal}12` : v === "logout" ? `${C.textLight}12` : v.includes("create") ? `${C.success}12` : v.includes("delete") ? `${C.danger}12` : `${C.warning}12`,
                color: v === "login" ? C.teal : v === "logout" ? C.textLight : v.includes("create") ? C.success : v.includes("delete") ? C.danger : C.warning,
              }}>{ACTION_LABELS[v] || v}</span>
            )},
            { key: "details", label: "التفاصيل" },
            { key: "ip", label: "IP", width: "110px", render: v => <span style={{ fontSize: 12, fontFamily: "monospace", color: C.textLight }}>{v}</span> },
          ]}
          data={filtered}
        />
      </Card>
    </div>
  );
}

/* ─── Sessions Tab ─── */
function SessionsTab() {
  return (
    <div style={{ marginTop: 16 }}>
      <div style={S.kpiRow}>
        <KPICard title="جلسات نشطة" value={DEMO_SESSIONS.length} icon={LogIn} color={C.teal} />
        <KPICard title="مستخدمون متصلون" value={[...new Set(DEMO_SESSIONS.map(s => s.user))].length} icon={Users} color={C.success} />
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            { key: "user", label: "المستخدم", render: (v, row) => (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.success }} />
                <span style={{ fontWeight: 600 }}>{v}</span>
                <span style={{ fontSize: 11, color: C.textLight }}>({ROLE_NAMES[row.role]})</span>
              </div>
            )},
            { key: "loginTime", label: "وقت الدخول", width: "150px", render: v => <span style={{ fontSize: 12, fontFamily: "monospace" }}>{v}</span> },
            { key: "ip", label: "عنوان IP", width: "120px", render: v => <span style={{ fontSize: 12, fontFamily: "monospace" }}>{v}</span> },
            { key: "device", label: "الجهاز", width: "140px" },
            { key: "duration", label: "المدة", width: "90px", render: v => <span style={{ color: C.teal, fontWeight: 600 }}>{v}</span> },
          ]}
          data={DEMO_SESSIONS}
        />
      </Card>
    </div>
  );
}

/* ═════ Demo Data ═════ */
const DEMO_USERS = [
  { id: "u1", name: "م. أحمد الخالدي", email: "exec@bnr.org.sa", phone: "0551001001", role: "exec", is_active: true, must_change_password: false, last_login: "1446/09/18 08:30", created_at: "1443/01/01" },
  { id: "u2", name: "سارة العتيبي", email: "secretary@bnr.org.sa", phone: "0552002002", role: "secretary", is_active: true, must_change_password: false, last_login: "1446/09/18 09:15", created_at: "1443/01/01" },
  { id: "u3", name: "خالد المطيري", email: "system@bnr.org.sa", phone: "0557007007", role: "system", is_active: true, must_change_password: false, last_login: "1446/09/18 07:45", created_at: "1443/01/01" },
  { id: "u4", name: "فهد الحربي", email: "finance@bnr.org.sa", phone: "0555005005", role: "finance_manager", is_active: true, must_change_password: false, last_login: "1446/09/17 14:20", created_at: "1443/03/15" },
  { id: "u5", name: "نورة الدوسري", email: "women@bnr.org.sa", phone: "0556006006", role: "women_manager", is_active: true, must_change_password: false, last_login: "1446/09/18 10:00", created_at: "1444/01/15" },
  { id: "u6", name: "عبدالرحمن السبيعي", email: "communities@bnr.org.sa", phone: "0553003003", role: "communities_manager", is_active: true, must_change_password: false, last_login: "1446/09/17 16:30", created_at: "1444/06/01" },
  { id: "u7", name: "يوسف الشمري", email: "mosques@bnr.org.sa", phone: "0554004004", role: "mosques_manager", is_active: true, must_change_password: false, last_login: "1446/09/16 11:45", created_at: "1443/09/10" },
  { id: "u8", name: "ناصر الغامدي", email: "hr@bnr.org.sa", phone: "0552202202", role: "hr_manager", is_active: true, must_change_password: false, last_login: "1446/09/18 08:00", created_at: "1444/03/01" },
  { id: "u9", name: "سلطان العسيري", email: "fund@bnr.org.sa", phone: "0551101101", role: "fundraising_manager", is_active: true, must_change_password: false, last_login: "1446/09/15 09:30", created_at: "1444/08/01" },
  { id: "u10", name: "تركي الزهراني", email: "comms@bnr.org.sa", phone: "0550100100", role: "comms_manager", is_active: true, must_change_password: false, last_login: "1446/09/17 13:00", created_at: "1445/06/15" },
  { id: "u11", name: "عمر البقمي", email: "volunteer@bnr.org.sa", phone: "0559009009", role: "volunteer_manager", is_active: true, must_change_password: false, last_login: "1446/09/14 10:15", created_at: "1445/01/01" },
  { id: "u12", name: "إبراهيم العنزي", email: "preacher1@bnr.org.sa", phone: "0553456789", role: "preacher", is_active: true, must_change_password: true, last_login: null, created_at: "1446/06/01" },
  { id: "u13", name: "ماجد القرني", email: "emp1@bnr.org.sa", phone: "0558008008", role: "employee", is_active: true, must_change_password: false, last_login: "1446/09/10 08:30", created_at: "1445/07/10" },
  { id: "u14", name: "حسن الدوسري", email: "old@bnr.org.sa", phone: "0551234999", role: "employee", is_active: false, must_change_password: false, last_login: "1445/12/01 10:00", created_at: "1443/06/01" },
];

const ACTION_LABELS = {
  login: "تسجيل دخول",
  logout: "تسجيل خروج",
  create_user: "إنشاء مستخدم",
  update_user: "تعديل مستخدم",
  delete_record: "حذف سجل",
  update_record: "تعديل سجل",
  create_record: "إنشاء سجل",
  password_change: "تغيير كلمة مرور",
  password_reset: "إعادة تعيين كلمة مرور",
  export_data: "تصدير بيانات",
};

const DEMO_AUDIT_LOG = [
  { id: "al1", timestamp: "1446/09/18 09:15:22", username: "سارة العتيبي", action: "login", details: "تسجيل دخول ناجح", ip: "192.168.1.105" },
  { id: "al2", timestamp: "1446/09/18 08:30:10", username: "أحمد الخالدي", action: "login", details: "تسجيل دخول ناجح", ip: "192.168.1.100" },
  { id: "al3", timestamp: "1446/09/18 08:45:33", username: "أحمد الخالدي", action: "update_record", details: "تعديل ميزانية قسم الجاليات", ip: "192.168.1.100" },
  { id: "al4", timestamp: "1446/09/18 07:45:05", username: "خالد المطيري", action: "login", details: "تسجيل دخول ناجح", ip: "192.168.1.110" },
  { id: "al5", timestamp: "1446/09/17 16:30:44", username: "عبدالرحمن السبيعي", action: "create_record", details: "إضافة داعية جديد: محمد الأحمدي", ip: "192.168.1.103" },
  { id: "al6", timestamp: "1446/09/17 15:20:18", username: "فهد الحربي", action: "create_record", details: "تسجيل إيراد — تبرع 50,000 ريال", ip: "192.168.1.104" },
  { id: "al7", timestamp: "1446/09/17 14:10:55", username: "ناصر الغامدي", action: "update_user", details: "تحديث بيانات الموظف عمر البقمي", ip: "192.168.1.108" },
  { id: "al8", timestamp: "1446/09/17 11:45:30", username: "خالد المطيري", action: "password_reset", details: "إعادة تعيين كلمة مرور: إبراهيم العنزي", ip: "192.168.1.110" },
  { id: "al9", timestamp: "1446/09/16 16:00:12", username: "أحمد الخالدي", action: "logout", details: "تسجيل خروج", ip: "192.168.1.100" },
  { id: "al10", timestamp: "1446/09/16 10:30:40", username: "تركي الزهراني", action: "create_record", details: "نشر خبر: حملة إفطار مليون صائم", ip: "192.168.1.107" },
  { id: "al11", timestamp: "1446/09/15 09:00:22", username: "سلطان العسيري", action: "export_data", details: "تصدير تقرير التبرعات الشهري", ip: "192.168.1.109" },
  { id: "al12", timestamp: "1446/09/14 14:20:50", username: "نورة الدوسري", action: "create_record", details: "إنشاء حلقة نسائية جديدة: حلقة البركة", ip: "192.168.1.106" },
];

const DEMO_SESSIONS = [
  { id: "s1", user: "أحمد الخالدي", role: "exec", loginTime: "1446/09/18 08:30", ip: "192.168.1.100", device: "Chrome — Windows", duration: "2:15" },
  { id: "s2", user: "سارة العتيبي", role: "secretary", loginTime: "1446/09/18 09:15", ip: "192.168.1.105", device: "Safari — MacOS", duration: "1:30" },
  { id: "s3", user: "خالد المطيري", role: "system", loginTime: "1446/09/18 07:45", ip: "192.168.1.110", device: "Firefox — Linux", duration: "3:00" },
  { id: "s4", user: "نورة الدوسري", role: "women_manager", loginTime: "1446/09/18 10:00", ip: "192.168.1.106", device: "Chrome — Android", duration: "0:45" },
  { id: "s5", user: "ناصر الغامدي", role: "hr_manager", loginTime: "1446/09/18 08:00", ip: "192.168.1.108", device: "Edge — Windows", duration: "2:45" },
];

/* ═════ Styles ═════ */
const S = {
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 20, marginBottom: 16 },
  toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" },
  searchBox: { display: "flex", alignItems: "center", gap: 8, background: C.bg, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`, flex: 1, maxWidth: 320 },
  searchInput: { border: "none", background: "transparent", outline: "none", fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%" },
  filterSelect: { padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, background: C.white, cursor: "pointer" },
  detailRow: { fontSize: 14, color: C.primary, lineHeight: 1.6 },

  tableAvatar: { width: 32, height: 32, borderRadius: 8, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 },
  userHeader: { display: "flex", alignItems: "center", gap: 20, paddingBottom: 14, borderBottom: `1px solid ${C.border}` },
  userAvatarLg: { width: 56, height: 56, borderRadius: 14, background: `linear-gradient(135deg, ${C.primary}, ${C.teal})`, color: C.white, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22, flexShrink: 0 },
  actionBtn: { background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, display: "flex", alignItems: "center" },
  permTag: { padding: "3px 8px", background: `#7c3aed10`, color: "#7c3aed", borderRadius: 6, fontSize: 11, fontWeight: 500 },
  permBadge: { padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600 },

  rolesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16 },
  roleCard: { padding: 18 },
  roleHeader: { display: "flex", alignItems: "center", gap: 12 },
  roleIcon: { width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  moduleTag: { padding: "2px 7px", background: C.bg, color: C.textLight, borderRadius: 4, fontSize: 10, fontWeight: 500, border: `1px solid ${C.border}` },
};
